import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  BarChart,
  Close,
  Delete,
  Edit,
  Link as LinkIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import { useSurvey } from '../../hooks/useSurvey';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

export const SURVEY_TYPES = ['popup', 'page'];
export const SURVEY_QUESTION_TYPES = ['rating', 'yesNo', 'singleChoice', 'multipleChoice', 'text'];

// German labels shared by the list, the form dialog and the statistics view.
export const SURVEY_TYPE_LABELS = {
  popup: 'Popup nach Buchung',
  page: 'Umfrageseite',
};

export const SURVEY_QUESTION_TYPE_LABELS = {
  rating: 'Bewertung (Skala)',
  yesNo: 'Ja/Nein',
  singleChoice: 'Einfachauswahl',
  multipleChoice: 'Mehrfachauswahl',
  text: 'Freitext',
};

const SLUG_RE = /^[a-z0-9-]{3,80}$/;

const isChoiceType = type => type === 'singleChoice' || type === 'multipleChoice';

// Derives a slug suggestion from the title (umlauts transliterated, kebab-case).
const slugFromTitle = title =>
  (title || '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

// One question of the builder: text, type, type-specific fields, required flag.
const SurveyQuestionEditor = ({
  question,
  index,
  total,
  questionTextRefs,
  ratingMaxRefs,
  optionValueRefs,
  onMove,
  onRemove,
  onTypeChange,
  onRequiredChange,
  onAddOption,
  onRemoveOption,
}) => {
  return (
    <Paper variant='outlined' sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='subtitle2' color='text.secondary'>
          Frage {index + 1}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size='small'
            onClick={() => onMove(index, index - 1)}
            disabled={index === 0}
            title='Nach oben verschieben'
          >
            <ArrowUpward fontSize='small' />
          </IconButton>
          <IconButton
            size='small'
            onClick={() => onMove(index, index + 1)}
            disabled={index === total - 1}
            title='Nach unten verschieben'
          >
            <ArrowDownward fontSize='small' />
          </IconButton>
          <IconButton
            size='small'
            onClick={() => onRemove(question.key)}
            disabled={total <= 1}
            title='Frage entfernen'
            sx={{ color: total <= 1 ? undefined : 'error.main' }}
          >
            <Delete fontSize='small' />
          </IconButton>
        </Box>
      </Box>
      <Stack spacing={2}>
        <TextField
          label='Fragetext'
          placeholder='Wie zufrieden warst du mit deinem Besuch?'
          defaultValue={question.initialText}
          inputRef={el => {
            questionTextRefs.current[question.key] = el;
          }}
          fullWidth
          required
          size='small'
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl size='small' fullWidth>
            <InputLabel id={`survey-question-type-${question.key}`}>Fragetyp</InputLabel>
            <Select
              labelId={`survey-question-type-${question.key}`}
              label='Fragetyp'
              value={question.questionType}
              onChange={e => onTypeChange(question.key, e.target.value)}
            >
              {SURVEY_QUESTION_TYPES.map(type => (
                <MenuItem key={type} value={type}>
                  {SURVEY_QUESTION_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {question.questionType === 'rating' && (
            <TextField
              label='Skala bis'
              type='number'
              defaultValue={question.initialRatingMax}
              inputRef={el => {
                ratingMaxRefs.current[question.key] = el;
              }}
              inputProps={{ min: 2, max: 10 }}
              size='small'
              fullWidth
            />
          )}
        </Stack>
        {isChoiceType(question.questionType) && (
          <Box>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
              Optionen (mindestens 2)
            </Typography>
            <Stack spacing={1}>
              {question.options.map((option, optionIndex) => (
                <Box key={option.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    placeholder={`Option ${optionIndex + 1}`}
                    defaultValue={option.initialValue}
                    inputRef={el => {
                      optionValueRefs.current[option.key] = el;
                    }}
                    size='small'
                    fullWidth
                  />
                  <IconButton
                    size='small'
                    onClick={() => onRemoveOption(question.key, option.key)}
                    disabled={question.options.length <= 2}
                    title='Option entfernen'
                  >
                    <Close fontSize='small' />
                  </IconButton>
                </Box>
              ))}
            </Stack>
            <Button
              size='small'
              startIcon={<Add />}
              onClick={() => onAddOption(question.key)}
              sx={{ mt: 1 }}
            >
              Option hinzufügen
            </Button>
          </Box>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={question.required}
              onChange={e => onRequiredChange(question.key, e.target.checked)}
            />
          }
          label='Pflichtfrage'
        />
      </Stack>
    </Paper>
  );
};

// Create/edit dialog — every survey field plus the question builder. Text inputs are
// uncontrolled (refs); type, active flag and the question structure are controlled state.
const SurveyDialog = ({ open, dialogKey, initialData, onClose, onSubmit, saving }) => {
  const isEdit = !!initialData?._id;

  const titleRef = useRef(null);
  const slugRef = useRef(null);
  const descriptionRef = useRef(null);
  const thankYouTextRef = useRef(null);
  const questionTextRefs = useRef({});
  const ratingMaxRefs = useRef({});
  const optionValueRefs = useRef({});
  const slugTouchedRef = useRef(false);
  const keyCounterRef = useRef(0);

  const [type, setType] = useState('popup');
  const [isActive, setIsActive] = useState(true);
  // Seeds the slug field's defaultValue — updated before a type switch unmounts it.
  const [initialSlug, setInitialSlug] = useState('');
  const [questions, setQuestions] = useState([]);

  const makeKey = useCallback(prefix => {
    keyCounterRef.current += 1;
    return `${prefix}-${keyCounterRef.current}`;
  }, []);

  const emptyQuestion = useCallback(
    () => ({
      key: makeKey('q'),
      questionType: 'rating',
      initialText: '',
      initialRatingMax: 5,
      required: true,
      options: [],
    }),
    [makeKey]
  );

  useEffect(() => {
    // Editing an existing survey never auto-overwrites its slug.
    slugTouchedRef.current = !!initialData?._id;
    setType(initialData?.type || 'popup');
    setIsActive(initialData ? initialData.isActive !== false : true);
    setInitialSlug(initialData?.slug || '');
    const initialQuestions = (initialData?.questions || []).map(question => ({
      key: makeKey('q'),
      _id: question._id,
      questionType: question.questionType,
      initialText: question.text || '',
      initialRatingMax: question.ratingMax || 5,
      required: question.required !== false,
      options: (question.options || []).map(value => ({
        key: makeKey('o'),
        initialValue: value,
      })),
    }));
    setQuestions(initialQuestions.length > 0 ? initialQuestions : [emptyQuestion()]);
  }, [initialData, open, makeKey, emptyQuestion]);

  // While the slug has not been touched by hand it follows the title.
  const handleTitleChange = useCallback(
    e => {
      if (isEdit || slugTouchedRef.current || !slugRef.current) return;
      slugRef.current.value = slugFromTitle(e.target.value);
    },
    [isEdit]
  );

  // Persist the current slug before a type switch so the value survives the
  // slug field unmounting when the type leaves 'page' and comes back.
  const handleTypeChange = useCallback(e => {
    if (slugRef.current) setInitialSlug(slugRef.current.value);
    setType(e.target.value);
  }, []);

  // When the type switches to 'page' the slug field mounts — while the slug has
  // not been touched by hand it is (re)seeded from the current title.
  useEffect(() => {
    if (isEdit || type !== 'page' || slugTouchedRef.current || !slugRef.current) return;
    slugRef.current.value = slugFromTitle(titleRef.current?.value || '');
  }, [type, isEdit]);

  const handleAddQuestion = useCallback(() => {
    setQuestions(prev => [...prev, emptyQuestion()]);
  }, [emptyQuestion]);

  const handleRemoveQuestion = useCallback(key => {
    setQuestions(prev => (prev.length > 1 ? prev.filter(q => q.key !== key) : prev));
  }, []);

  const handleMoveQuestion = useCallback((from, to) => {
    setQuestions(prev => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const handleQuestionTypeChange = useCallback(
    (key, newType) => {
      setQuestions(prev =>
        prev.map(question => {
          if (question.key !== key) return question;
          // Persist the current input values into the seeding state so the
          // conditional fields keep what was typed when a type switch
          // unmounts and remounts them.
          const ratingMaxEl = ratingMaxRefs.current[question.key];
          const next = {
            ...question,
            questionType: newType,
            initialRatingMax: ratingMaxEl ? ratingMaxEl.value : question.initialRatingMax,
            options: question.options.map(option => {
              const optionEl = optionValueRefs.current[option.key];
              return optionEl ? { ...option, initialValue: optionEl.value } : option;
            }),
          };
          // A choice question always needs at least two option rows to fill in.
          if (isChoiceType(newType) && next.options.length < 2) {
            next.options = [
              ...next.options,
              ...Array.from({ length: 2 - next.options.length }, () => ({
                key: makeKey('o'),
                initialValue: '',
              })),
            ];
          }
          return next;
        })
      );
    },
    [makeKey]
  );

  const handleRequiredChange = useCallback((key, required) => {
    setQuestions(prev =>
      prev.map(question => (question.key === key ? { ...question, required } : question))
    );
  }, []);

  const handleAddOption = useCallback(
    key => {
      setQuestions(prev =>
        prev.map(question =>
          question.key === key
            ? {
                ...question,
                options: [...question.options, { key: makeKey('o'), initialValue: '' }],
              }
            : question
        )
      );
    },
    [makeKey]
  );

  const handleRemoveOption = useCallback((questionKey, optionKey) => {
    setQuestions(prev =>
      prev.map(question =>
        question.key === questionKey && question.options.length > 2
          ? { ...question, options: question.options.filter(o => o.key !== optionKey) }
          : question
      )
    );
  }, []);

  // Validates the form, assembles the payload from refs + state and hands it to the container.
  const handleDialogSubmit = useCallback(() => {
    const title = (titleRef.current?.value || '').trim();
    if (!title) {
      toast.error('Bitte einen Titel angeben.');
      return;
    }
    const slug = (slugRef.current?.value || '').trim();
    if (type === 'page' && !SLUG_RE.test(slug)) {
      toast.error(
        'Der Slug muss 3–80 Zeichen lang sein und darf nur Kleinbuchstaben, Ziffern und Bindestriche enthalten.'
      );
      return;
    }
    if (questions.length === 0) {
      toast.error('Mindestens eine Frage ist erforderlich.');
      return;
    }

    const builtQuestions = [];
    for (const question of questions) {
      const text = (questionTextRefs.current[question.key]?.value || '').trim();
      if (!text) {
        toast.error('Bitte für jede Frage einen Fragetext angeben.');
        return;
      }
      const built = { text, questionType: question.questionType, required: question.required };
      // Existing questions keep their _id so stored answers stay linked.
      if (question._id) built._id = question._id;
      if (isChoiceType(question.questionType)) {
        const options = question.options
          .map(option => (optionValueRefs.current[option.key]?.value || '').trim())
          .filter(value => value !== '');
        if (options.length < 2) {
          toast.error('Auswahl-Fragen brauchen mindestens zwei ausgefüllte Optionen.');
          return;
        }
        // The backend rejects duplicate options — catch them client-side.
        if (new Set(options).size !== options.length) {
          toast.error('Optionen müssen eindeutig sein.');
          return;
        }
        built.options = options;
      }
      if (question.questionType === 'rating') {
        const ratingMax = Number(ratingMaxRefs.current[question.key]?.value);
        if (!Number.isInteger(ratingMax) || ratingMax < 2 || ratingMax > 10) {
          toast.error('Die Bewertungsskala muss zwischen 2 und 10 liegen.');
          return;
        }
        built.ratingMax = ratingMax;
      }
      builtQuestions.push(built);
    }

    const payload = {
      title,
      description: (descriptionRef.current?.value || '').trim(),
      thankYouText: (thankYouTextRef.current?.value || '').trim(),
      isActive,
      questions: builtQuestions,
    };
    // The type is immutable — it is only part of the create payload.
    if (isEdit) {
      if (initialData.type === 'page') payload.slug = slug;
    } else {
      payload.type = type;
      if (type === 'page') payload.slug = slug;
    }
    onSubmit(payload);
  }, [initialData, isActive, isEdit, onSubmit, questions, type]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>{isEdit ? 'Umfrage bearbeiten' : 'Neue Umfrage'}</DialogTitle>
      <DialogContent>
        <Box key={dialogKey} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label='Titel'
            placeholder='Titel der Umfrage'
            defaultValue={initialData?.title || ''}
            inputRef={titleRef}
            onChange={handleTitleChange}
            fullWidth
            required
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='center'>
            <FormControl fullWidth>
              <InputLabel id='survey-dialog-type'>Typ</InputLabel>
              <Select
                labelId='survey-dialog-type'
                label='Typ'
                value={type}
                onChange={handleTypeChange}
                disabled={isEdit}
              >
                {SURVEY_TYPES.map(surveyType => (
                  <MenuItem key={surveyType} value={surveyType}>
                    {SURVEY_TYPE_LABELS[surveyType]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch checked={isActive} onChange={e => setIsActive(e.target.checked)} />
              }
              label='Aktiv'
              sx={{ whiteSpace: 'nowrap' }}
            />
          </Stack>
          {isEdit && (
            <Typography variant='caption' color='text.secondary'>
              Der Typ kann nachträglich nicht geändert werden.
            </Typography>
          )}
          {type === 'popup' && (
            <Typography variant='caption' color='text.secondary'>
              Es wird die zuletzt erstellte aktive Popup-Umfrage nach der Buchung angezeigt.
            </Typography>
          )}
          {type === 'page' && (
            <TextField
              label='Slug'
              placeholder='z. B. sommer-feedback'
              defaultValue={initialSlug}
              inputRef={slugRef}
              onChange={() => {
                slugTouchedRef.current = true;
              }}
              helperText='Bestimmt den Link der Umfrageseite: /survey/<slug> — 3–80 Zeichen, Kleinbuchstaben, Ziffern und Bindestriche.'
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
          )}
          <TextField
            label='Beschreibung'
            placeholder='Einleitungstext über den Fragen (optional)'
            defaultValue={initialData?.description || ''}
            inputRef={descriptionRef}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label='Dankestext'
            placeholder='Vielen Dank für dein Feedback!'
            defaultValue={initialData?.thankYouText || ''}
            inputRef={thankYouTextRef}
            fullWidth
            multiline
            minRows={2}
          />
          <Typography variant='subtitle2'>Fragen</Typography>
          {questions.map((question, index) => (
            <SurveyQuestionEditor
              key={question.key}
              question={question}
              index={index}
              total={questions.length}
              questionTextRefs={questionTextRefs}
              ratingMaxRefs={ratingMaxRefs}
              optionValueRefs={optionValueRefs}
              onMove={handleMoveQuestion}
              onRemove={handleRemoveQuestion}
              onTypeChange={handleQuestionTypeChange}
              onRequiredChange={handleRequiredChange}
              onAddOption={handleAddOption}
              onRemoveOption={handleRemoveOption}
            />
          ))}
          <Box>
            <Button variant='outlined' startIcon={<Add />} onClick={handleAddQuestion}>
              Frage hinzufügen
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button onClick={handleDialogSubmit} variant='contained' disabled={saving}>
          {isEdit ? 'Speichern' : 'Erstellen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Management list of all surveys (booking-widget popups + standalone survey pages).
function Surveys() {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const { fetchSurveys, createSurvey, updateSurvey, deleteSurvey } = useSurvey();

  const canRead = authCtx.checkRight('survey:read');
  const canManage = authCtx.checkRight('survey:manage');
  const canStats = authCtx.checkRight('survey:statistics');

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [dialogInitialData, setDialogInitialData] = useState(null);
  const [dialogKey, setDialogKey] = useState('new');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchSurveys();
      setSurveys(result?.surveys || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Umfragen.');
    } finally {
      setLoading(false);
    }
  }, [fetchSurveys]);

  useEffect(() => {
    if (!canRead) return;
    loadData();
  }, [canRead, loadData]);

  const handleOpenDialog = useCallback(survey => {
    if (survey) {
      setEditingSurvey(survey);
      setDialogInitialData(survey);
      setDialogKey(survey._id);
    } else {
      setEditingSurvey(null);
      setDialogInitialData(null);
      setDialogKey('new');
    }
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setEditingSurvey(null);
    setDialogInitialData(null);
    setDialogKey('new');
  }, []);

  const handleSubmit = useCallback(
    async payload => {
      setSaving(true);
      try {
        if (editingSurvey) {
          await updateSurvey(editingSurvey._id, payload);
        } else {
          await createSurvey(payload);
        }
        handleCloseDialog();
        loadData();
      } catch (error) {
        console.error('Error saving survey:', error);
      } finally {
        setSaving(false);
      }
    },
    [createSurvey, editingSurvey, handleCloseDialog, loadData, updateSurvey]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteSurvey(deleteTarget._id);
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting survey:', error);
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteSurvey, deleteTarget, isDeleting, loadData]);

  const copyPageLink = useCallback(survey => {
    if (!survey.pageUrl) return;
    navigator.clipboard
      .writeText(survey.pageUrl)
      .then(() => {
        toast.success('Link kopiert');
      })
      .catch(() => {
        toast.error('Kopieren fehlgeschlagen');
      });
  }, []);

  const openStatistics = useCallback(
    survey => {
      navigate(`/surveyStatistics?surveyId=${survey._id}`);
    },
    [navigate]
  );

  if (!canRead) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='h5' component='h1'>
          Umfragen
        </Typography>
        {canManage && (
          <Button variant='contained' startIcon={<Add />} onClick={() => handleOpenDialog(null)}>
            Neue Umfrage
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : surveys.length === 0 ? (
        <Alert severity='info'>Noch keine Umfragen angelegt.</Alert>
      ) : (
        <TableContainer component={Paper} variant='outlined'>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Titel</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='right'>Fragen</TableCell>
                <TableCell align='right'>Antworten</TableCell>
                <TableCell>Erstellt</TableCell>
                <TableCell align='right'>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {surveys.map(survey => (
                <TableRow key={survey._id}>
                  <TableCell>
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      {survey.title || '—'}
                    </Typography>
                    {survey.type === 'page' && survey.slug && (
                      <Typography variant='caption' color='text.secondary'>
                        /survey/{survey.slug}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={SURVEY_TYPE_LABELS[survey.type] || survey.type}
                      size='small'
                      variant='outlined'
                      color={survey.type === 'page' ? 'info' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {survey.isActive ? (
                      <Chip label='Aktiv' size='small' color='success' />
                    ) : (
                      <Chip label='Inaktiv' size='small' variant='outlined' />
                    )}
                  </TableCell>
                  <TableCell align='right'>{survey.questions?.length || 0}</TableCell>
                  <TableCell align='right'>{survey.responseCount || 0}</TableCell>
                  <TableCell>
                    {survey.createdAt ? dayjs(survey.createdAt).format('DD.MM.YYYY') : '—'}
                  </TableCell>
                  <TableCell align='right'>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {canStats && (
                        <IconButton
                          size='small'
                          onClick={() => openStatistics(survey)}
                          title='Statistik öffnen'
                        >
                          <BarChart fontSize='small' />
                        </IconButton>
                      )}
                      {survey.type === 'page' && survey.pageUrl && (
                        <IconButton
                          size='small'
                          onClick={() => copyPageLink(survey)}
                          title='Link kopieren'
                        >
                          <LinkIcon fontSize='small' />
                        </IconButton>
                      )}
                      {canManage && (
                        <>
                          <IconButton
                            size='small'
                            onClick={() => handleOpenDialog(survey)}
                            title='Bearbeiten'
                          >
                            <Edit fontSize='small' />
                          </IconButton>
                          <IconButton
                            size='small'
                            onClick={() => setDeleteTarget(survey)}
                            title='Löschen'
                            sx={{ color: 'error.main' }}
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete confirmation — deleting a survey also deletes all its responses. */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Umfrage löschen</DialogTitle>
        <DialogContent>
          Soll die Umfrage „{deleteTarget?.title || ''}“ wirklich gelöscht werden? Dabei werden
          auch alle bereits abgegebenen Antworten ({deleteTarget?.responseCount || 0})
          unwiderruflich gelöscht.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button
            color='error'
            variant='contained'
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            sx={{ backgroundColor: 'error.main', '&:hover': { backgroundColor: 'error.dark' } }}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <SurveyDialog
        open={openDialog}
        dialogKey={dialogKey}
        initialData={dialogInitialData}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        saving={saving}
      />
    </Box>
  );
}

export default Surveys;
